import { useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import {
  ERROR_COSTS,
  REFUND_BONUS,
  SOURCE_LABELS,
  type ErrorSource,
} from "@/lib/errorCosts";

interface RefundDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: ErrorSource;
  sourceId: string;
  targetUserId: string;
  targetUserEmail?: string | null;
  errorSummary?: string;
  onSuccess?: () => void;
}

export function RefundDialog({
  open,
  onOpenChange,
  source,
  sourceId,
  targetUserId,
  targetUserEmail,
  errorSummary,
  onSuccess,
}: RefundDialogProps) {
  const [actionCost, setActionCost] = useState<number>(ERROR_COSTS[source] ?? 1);
  const [reason, setReason] = useState<string>(errorSummary?.slice(0, 200) || "");
  const [submitting, setSubmitting] = useState(false);

  const refundTotal = Math.max(1, Math.round(actionCost || 1)) + REFUND_BONUS;

  const handleSubmit = async () => {
    if (!targetUserId) {
      toast({ title: "Missing user ID", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "admin-refund-error-credits",
        {
          body: {
            source,
            source_id: sourceId,
            target_user_id: targetUserId,
            action_cost: actionCost,
            error_summary: reason,
          },
        }
      );
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);

      toast({
        title: `Refunded ${refundTotal} credits`,
        description: targetUserEmail
          ? `Email sent to ${targetUserEmail}`
          : "Credits added to user account",
      });
      onSuccess?.();
      onOpenChange(false);
    } catch (err: any) {
      toast({
        title: "Refund failed",
        description: err?.message || String(err),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Refund credits to user</DialogTitle>
          <DialogDescription>
            Refund credits to{" "}
            <strong>{targetUserEmail || targetUserId}</strong> for a{" "}
            <strong>{SOURCE_LABELS[source]}</strong> error. They will receive
            an apology email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-success/30 bg-success/5 p-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                Total refund
              </p>
              <p className="text-2xl font-semibold tabular-nums text-success mt-0.5">
                {refundTotal} <span className="text-sm font-normal text-muted-foreground">credits</span>
              </p>
            </div>
            <div className="text-right text-xs text-muted-foreground space-y-0.5">
              <div>Action cost: <span className="tabular-nums">{actionCost || 0}</span></div>
              <div>Bonus: <span className="tabular-nums">+{REFUND_BONUS}</span></div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="action-cost">Original action cost</Label>
            <Input
              id="action-cost"
              type="number"
              min={1}
              max={100}
              value={actionCost}
              onChange={(e) => setActionCost(Number(e.target.value))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason / error summary (optional)</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Brief description for audit trail"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Refund {refundTotal} credits
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
